import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory, getLatestArticles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'zuixin');
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      setPage(1);
    }
  }, [slug]);

  const category = getCategoryBySlug(activeSlug);
  const allArticles = activeSlug === 'zuixin'
    ? getLatestArticles(20)
    : getArticlesByCategory(activeSlug);
  const perPage = 8;
  const totalPages = Math.ceil(allArticles.length / perPage);
  const displayedArticles = allArticles.slice((page - 1) * perPage, page * perPage);

  const pageTitle = activeSlug === 'zuixin' ? '最新爆料' : `${category?.name} - 51黑料`;
  const pageDesc = activeSlug === 'zuixin'
    ? '51黑料最新爆料频道，24小时实时更新全网最劲爆的黑料资讯，汇聚明星、网红、娱乐圈最新动态，让你第一时间掌握热点真相。'
    : `${category?.description}。51黑料聚合平台为您实时更新${category?.name}相关内容。`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDesc,
    url: `https://51heiliao.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '51黑料',
      url: 'https://51heiliao.com/',
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: displayedArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://51heiliao.com/article/${article.slug}`,
        name: article.title,
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`51黑料,${category?.name || '最新爆料'},51黑料爆料,51黑料聚合,黑料资讯`}
        canonicalUrl={`https://51heiliao.com/category/${activeSlug}`}
      />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <main className="pt-20 pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '分类' },
              ]}
            />
          </div>

          {/* Category Header */}
          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {activeSlug === 'zuixin' ? '最新爆料' : category?.name}
            </h1>
            <p className="text-foreground-500 text-sm">{category?.description}</p>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide mb-8 pb-1">
            <Link
              to="/category/zuixin"
              onClick={() => { setActiveSlug('zuixin'); setPage(1); }}
              className={`flex-shrink-0 px-5 py-2.5 text-sm font-medium rounded-full transition-all whitespace-nowrap ${
                activeSlug === 'zuixin'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className="ri-flashlight-line mr-1.5"></i>最新爆料
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => { setActiveSlug(cat.slug); setPage(1); }}
                className={`flex-shrink-0 px-5 py-2.5 text-sm font-medium rounded-full transition-all whitespace-nowrap ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>{cat.name}
              </Link>
            ))}
          </div>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {displayedArticles.map((article) => (
              <article key={article.id} className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all group">
                <Link to={`/article/${article.slug}`} className="block">
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-3 text-xs text-foreground-500 mb-2">
                      <span className="px-2 py-0.5 bg-primary-50 text-primary-700 rounded whitespace-nowrap">{article.categoryName}</span>
                      <span>{article.date}</span>
                    </div>
                    <h2 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mb-2">
                      {article.title}
                    </h2>
                    <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-foreground-400">
                      <span><i className="ri-eye-line mr-1"></i>{(article.views / 10000).toFixed(1)}万</span>
                      <span>{article.author}</span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                    page === p
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}

          {displayedArticles.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">暂无相关内容</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}